import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated } from '../redux/slices/authSlice';
import {
  useClaimShareBundleMutation,
  useLazyGetShareBundleByTokenQuery
} from '../redux/apis/endpoints/shareBundleApi';
import { enhancedClosetApi } from '../redux/apis/endpoints/closetApi';
import { extractShareToken } from '../utils/shareLink';

const PENDING_SHARE_KEY = 'pending_share_token';
const SEEN_CLIPBOARD_KEY = 'dismissed_share_tokens';

const readDismissed = () => {
  try {
    const raw = localStorage.getItem(SEEN_CLIPBOARD_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const rememberDismissed = (token) => {
  try {
    const list = readDismissed();
    if (!list.includes(token)) {
      // Keep the list short — this is nag-suppression, not history
      localStorage.setItem(SEEN_CLIPBOARD_KEY, JSON.stringify([...list, token].slice(-25)));
    }
  } catch {
    /* storage unavailable — worst case we re-prompt once */
  }
};

/**
 * Recipient side of the share flow.
 *
 * 1. Detects `?share=<token>` in the URL, or a share link sitting on the
 *    clipboard, and stores the token.
 * 2. Logged out → surfaces a preview so the login prompt can show what's coming.
 * 3. Logged in → claims the bundle and hands back data for the success modal.
 *
 * Works for every bundle type (selection / closet / lookbook / single look) and
 * still resolves legacy single-result tokens, which the backend folds in.
 */
export const useShareFlow = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();
  const [claimShareBundle] = useClaimShareBundleMutation();
  const [fetchShareByToken] = useLazyGetShareBundleByTokenQuery();

  const [shareModalData, setShareModalData] = useState(null);
  const [profileShare, setProfileShare] = useState(null);
  // Hiding the prompt must not forget the share — the whole point of storing the
  // token is that it still imports once the recipient signs in.
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingToken, setPendingToken] = useState(
    () => localStorage.getItem(PENDING_SHARE_KEY) || null
  );

  // Clipboard-detected link awaiting an explicit "add to my closet"
  const [clipboardShare, setClipboardShare] = useState(null);

  const claimedTokensRef = useRef(new Set());

  // ---------------------------------------------------------------- URL token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareToken = params.get('share');
    if (!shareToken) return;

    localStorage.setItem(PENDING_SHARE_KEY, shareToken);
    setPendingToken(shareToken);

    const url = new URL(window.location.href);
    url.searchParams.delete('share');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, []);

  // Preview for the pre-login prompt (public endpoint)
  // Resolve the pending token exactly once, logged in or not. A live
  // subscription here proved fragile for guests — the app fires authenticated
  // queries on load whose 401 handling churns store state, and the query could
  // sit in `pending` forever even though the request had returned 200.
  const [pendingShareInfo, setPendingShareInfo] = useState(null);

  useEffect(() => {
    if (!pendingToken || pendingShareInfo) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const info = await fetchShareByToken(pendingToken).unwrap();
        if (cancelled) return;

        // A profile link is a look at someone, not a transfer: show it straight
        // away, with no login gate and nothing entering anyone's closet.
        if (info?.sourceType === 'profile') {
          setProfileShare(info.profile);
          localStorage.removeItem(PENDING_SHARE_KEY);
          setPendingToken(null);
          return;
        }

        setPendingShareInfo(info || null);
      } catch (error) {
        console.error('[ShareFlow] Could not resolve share link:', error);
        if (!cancelled && error?.status === 404) {
          localStorage.removeItem(PENDING_SHARE_KEY);
          setPendingToken(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingToken, pendingShareInfo, fetchShareByToken]);

  // ------------------------------------------------------------------- claim
  const claimToken = useCallback(
    async (token) => {
      if (!token || claimedTokensRef.current.has(token)) return;
      claimedTokensRef.current.add(token);
      setIsProcessing(true);

      try {
        const result = await claimShareBundle(token).unwrap();

        setShareModalData({
          sharerName: result.sharer?.displayName || result.sharer?.username || 'A friend',
          title: result.title,
          sourceType: result.sourceType,
          closet: result.closet,
          items: result.claimedItems || []
        });

        // RTK Query APIs are separate instances, so the claim mutation's own
        // invalidation can't reach the closet cache — nudge it directly or the
        // new items don't appear until a reload.
        dispatch(enhancedClosetApi.util.invalidateTags(['ClosetClothes', 'Closets']));
        localStorage.removeItem(PENDING_SHARE_KEY);
        setPendingToken(null);
      } catch (error) {
        console.error('[ShareFlow] Failed to claim shared items:', error);
        // Preserve the token for a retry on reload. Keep the attempt guard so a
        // failing endpoint cannot trigger an unbounded automatic retry loop.
      } finally {
        setIsProcessing(false);
      }
    },
    [claimShareBundle, dispatch]
  );

  // Claim once we know the link carries items and the user is authenticated
  useEffect(() => {
    if (!pendingToken || !pendingShareInfo || !isAuthenticated || isProcessing) return;
    claimToken(pendingToken);
  }, [isAuthenticated, pendingToken, pendingShareInfo, isProcessing, claimToken]);

  // --------------------------------------------------------- clipboard detect
  const inspectText = useCallback(
    async (text) => {
      const token = extractShareToken(text);
      if (!token) return;
      if (token === pendingToken) return;
      if (claimedTokensRef.current.has(token)) return;
      if (readDismissed().includes(token)) return;

      try {
        const info = await fetchShareByToken(token).unwrap();
        if (info) setClipboardShare({ token, info });
      } catch {
        // Expired, revoked, or not ours — stay quiet
      }
    },
    [fetchShareByToken, pendingToken]
  );

  useEffect(() => {
    let cancelled = false;

    // Only auto-read when the browser has already granted clipboard access.
    // Probing otherwise would fire a permission prompt out of nowhere.
    const tryReadClipboard = async () => {
      if (!navigator.clipboard?.readText || !navigator.permissions?.query) return;
      try {
        const status = await navigator.permissions.query({ name: 'clipboard-read' });
        if (status.state !== 'granted') return;
        const text = await navigator.clipboard.readText();
        if (!cancelled) inspectText(text);
      } catch {
        /* unsupported browser or read denied */
      }
    };

    // A paste anywhere in the app needs no permission at all
    const onPaste = (e) => {
      const text = e.clipboardData?.getData('text');
      if (text) inspectText(text);
    };

    const onFocus = () => tryReadClipboard();

    tryReadClipboard();
    window.addEventListener('focus', onFocus);
    document.addEventListener('paste', onPaste);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('paste', onPaste);
    };
  }, [inspectText]);

  const acceptClipboardShare = useCallback(() => {
    if (!clipboardShare) return;
    const { token } = clipboardShare;
    setClipboardShare(null);

    if (isAuthenticated) {
      claimToken(token);
    } else {
      // Route it through the normal pending-token path so the login prompt shows
      localStorage.setItem(PENDING_SHARE_KEY, token);
      setPendingToken(token);
    }
  }, [clipboardShare, isAuthenticated, claimToken]);

  const dismissClipboardShare = useCallback(() => {
    if (clipboardShare) rememberDismissed(clipboardShare.token);
    setClipboardShare(null);
  }, [clipboardShare]);

  // ------------------------------------------------------------------ exports
  const closeShareModal = useCallback(() => setShareModalData(null), []);

  // Closes the prompt only. The token survives, so signing in later still
  // imports; it is cleared on a successful claim or a dead link.
  const dismissPendingShare = useCallback(() => setPromptDismissed(true), []);

  const hasPendingShare =
    !!pendingToken && !isAuthenticated && !!pendingShareInfo && !promptDismissed;

  return {
    shareModalData,
    closeShareModal,
    isProcessingShare: isProcessing,
    hasPendingShare,
    pendingShareInfo: hasPendingShare ? pendingShareInfo : null,
    dismissPendingShare,
    clipboardShare,
    acceptClipboardShare,
    dismissClipboardShare,
    profileShare,
    closeProfileShare: () => setProfileShare(null)
  };
};

export default useShareFlow;
